import { ResponsiveText } from '@eventbrite/wagtail-components';
import React from 'react';
import { useTheme } from '../../../utils/context/theme-context';
import {
    WagtailModulePagesBreadCrumb,
    WagtailModulePagesBreadCrumbItem,
} from '../../../utils/WagtailModulePagesBreadCrumb';
import CTABlock, {
    CTABlockInterface,
} from '../../sharedComponents/cta-block/CTABlock';
import { BaseModuleProps } from '../types';
import './standardCallToAction.scss';

export interface ImageBlock {
    imageUrl: string;
    altText: string;
    alignment: 'left' | 'right';
}

export interface StandardCallToActionProps {
    eyebrow?: string;
    headline?: string;
    bodyText: string;
    standardCtaGroup: CTABlockInterface[];
    moduleClass?: string;
    subText?: string;
    image: ImageBlock;
    breadcrumbs?: WagtailModulePagesBreadCrumbItem[];
}

const getAlignment = (alignment: string, content = false): any => {
    const contentAlignment = alignment === 'left' ? 'right' : 'left';
    return `aligned__${content ? contentAlignment : alignment}`;
};

const StandardCallToAction: React.FC<
    StandardCallToActionProps & BaseModuleProps
> = ({
    eyebrow,
    headline,
    bodyText,
    standardCtaGroup,
    image,
    subText,
    variant,
    breadcrumbs,
}) => {
    const baseOptions = { variant };
    const theme = useTheme();
    const [backgroundClass] = theme.generateStyles('background', baseOptions);
    const [textClass] = theme.generateStyles('text', baseOptions);

    return (
        <section className={`standard-cta-module ${backgroundClass}`}>
            <div className="module-landing-page__module-container-content">
                <div className="standard-cta-module__container">
                    <div
                        className={`standard-cta-module__image-container ${getAlignment(
                            image.alignment,
                        )}`}
                    >
                        <img src={image.imageUrl} alt={image.altText} />
                    </div>
                    <div
                        className={`standard-cta-module__content ${getAlignment(
                            image.alignment,
                            true,
                        )}`}
                    >
                        <div className="standard-cta-module__content__container">
                            {breadcrumbs ? (
                                <WagtailModulePagesBreadCrumb
                                    containerClasses={`standard-cta-module__breadcrumbs ${textClass}`}
                                    breadcrumbs={breadcrumbs}
                                />
                            ) : null}
                            {!breadcrumbs && eyebrow ? (
                                <ResponsiveText
                                    base={{ fontSize: 14, lineHeight: 20 }}
                                    small={{ fontSize: 12, lineHeight: 16 }}
                                    className={`standard-cta-module__content__eyebrow ${textClass}`}
                                >
                                    {eyebrow}
                                </ResponsiveText>
                            ) : null}
                            {headline && (
                                <header>
                                    <ResponsiveText
                                        component="h2"
                                        base={{ fontSize: 52, lineHeight: 64 }}
                                        small={{
                                            fontSize: 32,
                                            lineHeight: 40,
                                        }}
                                        className={`standard-cta-module__content__headline ${textClass}`}
                                    >
                                        {headline}
                                    </ResponsiveText>
                                </header>
                            )}
                            <ResponsiveText
                                component="main"
                                base={{ fontSize: 18, lineHeight: 24 }}
                                small={{ fontSize: 14, lineHeight: 20 }}
                                className={`standard-cta-module__content__bodyText ${textClass}`}
                            >
                                {bodyText}
                            </ResponsiveText>
                            <div className="standard-cta-module__content__cta-blocks">
                                {standardCtaGroup.map((blockProps, idx) => (
                                    <CTABlock
                                        key={idx}
                                        {...{
                                            ...blockProps,
                                            size: 'regular',
                                            variant: 'button',
                                            secondary: idx > 0,
                                            themeVariant: variant,
                                        }}
                                    />
                                ))}
                            </div>
                            <div
                                className={`standard-cta-module__content__subtext ${textClass}`}
                            >
                                {subText}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default StandardCallToAction;
